import { useState } from 'react'
import { FolderOpen, Plus, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { useWorkspace } from '@/contexts/WorkspaceContext'

export function WorkspaceWelcome() {
  const { workspaces, isLoading, createWorkspace, setActiveWorkspaceId } = useWorkspace()
  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  const handleCreate = async () => {
    const trimmed = newName.trim()
    if (!trimmed) return
    setIsCreating(true)
    try {
      await createWorkspace(trimmed)
      setNewName('')
      setCreateOpen(false)
    } finally {
      setIsCreating(false)
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // No workspaces — create first
  if (workspaces.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center max-w-sm px-6">
          <div className="h-12 w-12 rounded-full bg-pharma-primary/10 flex items-center justify-center">
            <FolderOpen className="h-6 w-6 text-pharma-primary" />
          </div>
          <h2 className="text-xl font-semibold tracking-tight">Create your first workspace</h2>
          <p className="text-sm text-muted-foreground">
            Workspaces organize your documents, sources, and audits. Create one to get started.
          </p>
          <Button
            className="bg-pharma-primary hover:bg-pharma-primary/90 text-white"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Workspace
          </Button>
        </div>

        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Create Workspace</DialogTitle>
            </DialogHeader>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Workspace name"
              className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreate() }}
              autoFocus
            />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button
                onClick={handleCreate}
                disabled={!newName.trim() || isCreating}
                className="bg-pharma-primary hover:bg-pharma-primary/90 text-white"
              >
                {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  // Multiple workspaces — selector grid
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="flex flex-col items-center gap-6 text-center max-w-lg px-6">
        <div className="h-12 w-12 rounded-full bg-pharma-primary/10 flex items-center justify-center">
          <FolderOpen className="h-6 w-6 text-pharma-primary" />
        </div>
        <h2 className="text-xl font-semibold tracking-tight">Select a workspace</h2>
        <div className="grid grid-cols-2 gap-3 w-full">
          {workspaces.map((ws) => (
            <button
              key={ws.id}
              onClick={() => setActiveWorkspaceId(ws.id)}
              className="flex flex-col items-start gap-1 rounded-lg border border-border p-4 text-left hover:border-pharma-primary/50 hover:bg-pharma-primary/5 transition-colors"
            >
              <span className="text-sm font-medium text-foreground truncate w-full">{ws.name}</span>
            </button>
          ))}
        </div>
        <Button
          variant="outline"
          className="text-pharma-primary border-pharma-primary/30 hover:bg-pharma-primary/5"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Workspace
        </Button>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Create Workspace</DialogTitle>
          </DialogHeader>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Workspace name"
            className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate() }}
            autoFocus
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={!newName.trim() || isCreating}
              className="bg-pharma-primary hover:bg-pharma-primary/90 text-white"
            >
              {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
